import type { ToolGroup } from '@/types/chat-agent-types';
import type { OrgNodeAccessConfig } from '@/services/admin/get-org-node-access-config';

const MCP_TOOL_SUFFIX = /_mcp_cap$/;

/**
 * Filters MCP tool groups by the org's node access config.
 *
 * Tools explicitly disallowed by the config are stripped from their group,
 * and groups left with no tools are hidden. Tools absent from the config
 * (and empty/missing configs) are kept, so runtime enforcement in the MCP
 * aggregator remains the source of truth.
 */
export function filterToolGroupsByOrgAccess(
  groups: ToolGroup[],
  config: OrgNodeAccessConfig | null,
): ToolGroup[] {
  const items = config?.items;
  if (!items || Object.keys(items).length === 0) {
    return groups;
  }

  return groups
    .map((group) => {
      const allowedTools = group.tools.filter((tool) => {
        const name = tool.replace(MCP_TOOL_SUFFIX, '');
        if (!Object.prototype.hasOwnProperty.call(items, name)) {
          return true;
        }
        const item = items[name];
        return item.allowed_by_capitol && item.enabled_by_org !== false;
      });
      if (allowedTools.length === group.tools.length) {
        return group;
      }
      return { ...group, tools: allowedTools, tool_count: allowedTools.length };
    })
    .filter((group) => group.tools.length > 0);
}
